import React from 'react';
import { Strip } from '../../Models/Article';
import { GroupItemType } from '../../Models/Dashboard/DashNewsItem';
import { AssetLinkGroup } from '../../Models/AssetLinkGroup';
import { AssetLinkButtonGroup } from '../KodanshaUIKit/KodanshaAvailable/KodanshaAvailable';
import Questions, { QuestionItem } from '../SupportCenter/Questions';
import HtmlContent from './HtmlContent';
import './GroupedItemCollectionStrip.scss';

interface GroupedItemCollectionStripParams {
    title: string;
    subTitle: string;
    subtitleLoggedOut: string;
    stripGroups: StripGroupsTypes[];
}

interface StripGroupsTypes {
    groupTitle: string;
    ordinal: number;
    type: GroupItemType;
    groupedItems: GroupedItem[];
    htmlAnchor?: string;
}

interface GroupedItem {
    ordinal: number;

    // ExpandableEntry
    titleQuestion: string;
    textType: string;
    answerText: string;
    htmlAnchor?: string;

    // ButtonGroup
    linkText: string;
    linkUrl: string;
}

interface GroupedItemCollectionStripProps {
    strip: Strip;
}

export default function GroupedItemCollectionStrip({ strip: { params } }: GroupedItemCollectionStripProps) {
    const { title, subTitle, stripGroups } = params as GroupedItemCollectionStripParams;
    if (stripGroups.length === 0) {
        return null;
    }
    let content = null;
    // we assume that all items in the strip are of the same type
    switch (stripGroups[0].type) {
        case 'ExpandableEntry':
            content = <QuestionAnswerList title={title} subTitle={subTitle} stripGroups={stripGroups} />;
            break;
        case 'ButtonGroup':
            content = <ButtonGroupList title={title} stripGroups={stripGroups} />;
            break;
    }
    return (
        <div className='GroupedItemCollectionStrip'>
            {content}
        </div>
    );
}

function titleToId(title: string): string {
    return title.toLowerCase().split(" ").join("-");
}

interface QuestionAnswerListProps {
    title: string;
    subTitle: string;
    stripGroups: StripGroupsTypes[];
}

function QuestionAnswerList({ title, subTitle, stripGroups }: QuestionAnswerListProps) {
    return (
        <div className='QuestionAnswerList' id={titleToId(title)}>
            <div className='QuestionAnswerList-header'>
                {subTitle && <h3>{subTitle}</h3>}
                <h2>{title}</h2>
            </div>
            <div className='QuestionAnswerList-body'>
                {stripGroups.map((group, index) => (
                    <Questions key={index} title={group.groupTitle.toUpperCase()} id={group.htmlAnchor}>
                        {group.groupedItems.map((item, index) => {
                            switch (item.textType) {
                                case 'TinyMCE':
                                    return (
                                        <QuestionItem
                                            id={item.htmlAnchor}
                                            key={index}
                                            question={item.titleQuestion}
                                            answer={<HtmlContent content={item.answerText} />}
                                        />
                                    );
                                case 'RawHtml':
                                    return (
                                        <QuestionItem
                                            id={item.htmlAnchor}
                                            key={index}
                                            question={item.titleQuestion}
                                            answer={<div dangerouslySetInnerHTML={{ __html: item.answerText }} />}
                                        />
                                    );
                                case 'SimpleString':
                                    return (
                                        <QuestionItem
                                            id={item.htmlAnchor}
                                            key={index}
                                            question={item.titleQuestion}
                                            answer={<div>{item.answerText}</div>}
                                        />
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </Questions>
                ))}
            </div>
        </div>
    );
}

interface ButtonGroupListProps {
    title: string;
    stripGroups: StripGroupsTypes[];
}

function ButtonGroupList({ title, stripGroups }: ButtonGroupListProps) {
    return (
        <div className='ButtonGroupList' id={titleToId(title)}>
            <div className='ButtonGroupList-header'>
                <h2>{title}</h2>
            </div>
            <div className='ButtonGroupList-body'>
                {stripGroups.map((item, index) => {
                    const assetLinkGroupObj = AssetLinkGroup.createFromApi({
                        name: item.groupTitle,
                        assetLinks: item.groupedItems.map(({ linkUrl, linkText }) => ({
                            type: 'ExternalUrl',
                            enabled: true,
                            url: linkUrl,
                            name: linkText,
                        })),
                    });
                    return <AssetLinkButtonGroup key={index} item={assetLinkGroupObj} />;
                })}
            </div>
        </div>
    );
}
